import { useEffect, useRef } from 'react';
import { ActivityIndicator, Platform, StyleSheet, Text, View } from 'react-native';

import { Fonts, Palette } from '@/constants/theme';

/**
 * Visual sentinel rendered at the bottom of a windowed list, AND the
 * primary trigger for "load more" on web.
 *
 * Why the footer owns the trigger:
 *
 *   The hook can't reliably detect "user scrolled near bottom" on web
 *   because RN-Web's ScrollView doesn't actually fire scroll events
 *   when the window is the scroll surface (which is the case across
 *   our shell layout). IntersectionObserver attached to the footer's
 *   underlying DOM element doesn't care which element is scrolling —
 *   it just watches "did this view enter the viewport?" and fires
 *   when it does.
 *
 *   We use a `rootMargin: '300px 0px'` so the observer fires while
 *   the footer is still ~300px below the visible viewport. That gives
 *   the new batch a head start to paint before the user actually
 *   reaches the spinner, so the experience feels seamless rather than
 *   pause-then-content.
 *
 *   On native, RN doesn't have IntersectionObserver. The hook's own
 *   onScroll handler (bound to the parent ScrollView) handles the
 *   trigger there.
 *
 * Two visual states:
 *   - More to load: spinner + "Loading more…". Reads as "things are
 *     happening" even though the slice is synchronous.
 *   - All loaded: muted "That's all N items." so users know they
 *     reached the end of a long scroll. Hidden when the source set
 *     is empty (an empty state elsewhere handles that).
 */

interface Props {
  hasMore: boolean;
  total: number;
  /** Bound on web via IntersectionObserver; on native this is only
   *  used as a no-op since the parent ScrollView's onScroll handles
   *  the trigger. Required (not optional) because forgetting to pass
   *  it would silently break web infinite scroll. */
  loadMore: () => void;
  /** Singular noun for the end-state copy ("message", "expense"). */
  noun?: string;
  /** Plural override for irregular nouns ("entries" not "entrys"). */
  nounPlural?: string;
}

export function InfiniteFooter({
  hasMore,
  total,
  loadMore,
  noun = 'item',
  nounPlural,
}: Props) {
  // Web: attach IntersectionObserver to the footer's host element. The
  // RN View's `ref` on web gives us back the underlying DOM node, so
  // we can observe it directly. We re-attach when `loadMore` changes
  // (it's a useCallback that recreates when items.length changes).
  //
  // Native: this effect is a no-op — IntersectionObserver doesn't
  // exist. The parent ScrollView's onScroll handler does the work.
  const hostRef = useRef<View | null>(null);
  useEffect(() => {
    if (!hasMore) return;
    if (Platform.OS !== 'web') return;
    // eslint-disable-next-line no-undef
    if (typeof IntersectionObserver === 'undefined') return;
    const el = hostRef.current as unknown as HTMLElement | null;
    if (!el) return;
    // eslint-disable-next-line no-undef
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) loadMore();
        }
      },
      { rootMargin: '300px 0px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  if (total === 0) return null;

  if (hasMore) {
    return (
      <View ref={hostRef} style={styles.row}>
        <ActivityIndicator size="small" color={Palette.forest} />
        <Text style={styles.text}>Loading more…</Text>
      </View>
    );
  }
  const plural = nounPlural ?? `${noun}s`;
  return (
    <Text style={styles.endText}>
      That&apos;s all {total} {total === 1 ? noun : plural}.
    </Text>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center',
    paddingVertical: 22,
  },
  text: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
  },
  endText: {
    textAlign: 'center',
    fontFamily: Fonts.sansMedium,
    fontSize: 12,
    color: Palette.inkFaint,
    letterSpacing: 0.3,
    paddingVertical: 22,
  },
});
